import { DateTime } from 'luxon';

import type { VehicleAvailabilityTour } from '#domain/VehicleAvailability.ts';

import type { TourStop } from './TourStop.ts';

export type TimelineInterval = {
    start: DateTime;
    end: DateTime;
};

export type TimelineRange = TimelineInterval & {
    days: DateTime[];
};

const MINIMUM_VISIBLE_DAYS = 3;

const parseInterval = (start: string, end: string): TimelineInterval | undefined => {
    const parsedStart = DateTime.fromISO(start);
    const parsedEnd = DateTime.fromISO(end);
    return parsedStart.isValid && parsedEnd.isValid && parsedStart <= parsedEnd
        ? { start: parsedStart, end: parsedEnd }
        : undefined;
};

export const getProposedTourInterval = (stops: TourStop[]): TimelineInterval | undefined => {
    const intervals = stops.flatMap(stop => {
        const interval = parseInterval(stop.start, stop.end);
        return interval ? [interval] : [];
    });
    if (intervals.length === 0) {
        return undefined;
    }

    return {
        start: DateTime.fromMillis(Math.min(...intervals.map(interval => interval.start.toMillis()))),
        end: DateTime.fromMillis(Math.max(...intervals.map(interval => interval.end.toMillis()))),
    };
};

export const getTimelineRange = (
    proposedTour: TimelineInterval | undefined,
    now: DateTime = DateTime.local()
): TimelineRange => {
    if (!proposedTour) {
        const start = now.startOf('day');
        const end = start.plus({ days: MINIMUM_VISIBLE_DAYS });
        return {
            start,
            end,
            days: Array.from({ length: MINIMUM_VISIBLE_DAYS }, (_, index) => start.plus({ days: index })),
        };
    }

    let start = proposedTour.start.startOf('day');
    let end = proposedTour.end.startOf('day').plus({ days: 1 });
    const visibleDays = Math.max(1, Math.round(end.diff(start, 'days').days));
    const missingDays = Math.max(0, MINIMUM_VISIBLE_DAYS - visibleDays);

    start = start.minus({ days: Math.floor(missingDays / 2) });
    end = end.plus({ days: Math.ceil(missingDays / 2) });

    const dayCount = Math.round(end.diff(start, 'days').days);
    return {
        start,
        end,
        days: Array.from({ length: dayCount }, (_, index) => start.plus({ days: index })),
    };
};

export const intervalsOverlap = (first: TimelineInterval, second: TimelineInterval) => {
    const firstIsPoint = first.start.toMillis() === first.end.toMillis();
    const secondIsPoint = second.start.toMillis() === second.end.toMillis();
    if (firstIsPoint && secondIsPoint) {
        return first.start.toMillis() === second.start.toMillis();
    }
    if (firstIsPoint) {
        return first.start >= second.start && first.start < second.end;
    }
    if (secondIsPoint) {
        return second.start >= first.start && second.start < first.end;
    }
    return first.start < second.end && first.end > second.start;
};

export const getConflictingTours = (
    tours: VehicleAvailabilityTour[],
    proposedTour: TimelineInterval | undefined,
    excludedTourId?: string
) => {
    if (!proposedTour) {
        return [];
    }

    return tours.filter(tour => {
        if (tour.id === excludedTourId) {
            return false;
        }
        const tourInterval = parseInterval(tour.start, tour.end);
        return tourInterval ? intervalsOverlap(tourInterval, proposedTour) : false;
    });
};

const getCalendarDayPosition = (date: DateTime, range: TimelineRange): number => {
    if (date <= range.start) {
        return 0;
    }
    if (date >= range.end) {
        return range.days.length;
    }

    const dayStart = date.setZone(range.start.zone).startOf('day');
    const dayEnd = dayStart.plus({ days: 1 });
    // Each calendar day has equal width, including the 23/25-hour days at DST changes.
    const fraction = (date.toMillis() - dayStart.toMillis()) / (dayEnd.toMillis() - dayStart.toMillis());
    return Math.round(dayStart.diff(range.start, 'days').days) + fraction;
};

export const getTimelineBarPosition = (interval: TimelineInterval, range: TimelineRange) => {
    const visibleStart = getCalendarDayPosition(interval.start, range);
    const visibleEnd = getCalendarDayPosition(interval.end, range);

    return {
        left: `${(visibleStart / range.days.length) * 100}%`,
        width: `${(Math.max(0, visibleEnd - visibleStart) / range.days.length) * 100}%`,
    };
};

export const toTimelineInterval = (tour: VehicleAvailabilityTour) => parseInterval(tour.start, tour.end);

export const getTimelineGuides = (range: TimelineRange) =>
    range.days.flatMap(day =>
        [0, 6, 12, 18].map(hour => {
            const time = day.set({ hour });
            return { time, hour, left: getTimelineBarPosition({ start: time, end: time }, range).left };
        })
    );

export const getTimelineTourLayout = (tours: VehicleAvailabilityTour[]) => {
    const rowIntervals: TimelineInterval[] = [];
    const entries = tours
        .flatMap(tour => {
            const interval = toTimelineInterval(tour);
            return interval ? [{ tour, interval }] : [];
        })
        .sort((a, b) => a.interval.start.toMillis() - b.interval.start.toMillis() || a.tour.id.localeCompare(b.tour.id))
        .map(entry => {
            const availableRow = rowIntervals.findIndex(
                previous => previous.end <= entry.interval.start && !intervalsOverlap(previous, entry.interval)
            );
            const row = availableRow === -1 ? rowIntervals.length : availableRow;
            rowIntervals[row] = entry.interval;
            return { ...entry, row };
        });

    return { entries, rowCount: rowIntervals.length };
};
